'use client'

import React, { useState, useEffect, useCallback } from 'react'
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover'
import { Button } from '../ui/button'
import { Calendar } from '../ui/calendar'
import { cn } from '@/shared/lib/utils'
import { CalendarIcon, X } from 'lucide-react'
import { format } from 'date-fns'
import type { DateRange } from 'react-day-picker'
import { useIsMobile } from '@/shared/hooks/use-mobile'
import { Label } from '../ui/label'

export type DateRangePickerValue = {
  from?: Date
  to?: Date
}

export function parseYmdToLocalDate(ymd: string | undefined | null): Date | undefined {
  const s = (ymd ?? '').trim().slice(0, 10)
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return undefined
  const [y, m, d] = s.split('-').map(Number)
  return new Date(y, m - 1, d)
}

export function formatLocalDateYmd(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

export type DateRangePickerControlProps = {
  value: DateRangePickerValue
  onChange: (next: DateRangePickerValue) => void
  label?: string
  disabled?: boolean
  error?: boolean
  className?: string
  triggerClassName?: string
  placeholder?: string
  /** For debug logs / FormDateRangeInput integration */
  name?: string
  id?: string
  /** RHF field ref on the trigger button */
  triggerRef?: React.Ref<HTMLButtonElement>
}

/**
 * Shared trip-style date range UI (dual-month calendar, clear, iOS-in-dialog behavior).
 * Use with plain state or compose into FormDateRangeInput for react-hook-form.
 */
export function DateRangePickerControl({
  value,
  onChange,
  label,
  disabled = false,
  error = false,
  className,
  triggerClassName,
  placeholder = 'Pick a date',
  name,
  id,
  triggerRef,
}: DateRangePickerControlProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [calendarKey, setCalendarKey] = useState(0)
  const isMobile = useIsMobile()

  const [debug, setDebug] = useState(false)
  const [tapCount, setTapCount] = useState(0)
  const tapTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  const openedByTouchRef = React.useRef(false)

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search)
      const fromUrl = (params.get('debug') || params.get('Debug')) === 'datepicker'
      const fromStorage = sessionStorage.getItem('debug-datepicker') === '1'
      setDebug(fromUrl || fromStorage)
    }
  }, [])

  const handleDebugTap = useCallback(() => {
    setTapCount((c) => {
      const next = c + 1
      if (tapTimerRef.current) clearTimeout(tapTimerRef.current)
      tapTimerRef.current = setTimeout(() => setTapCount(0), 800)
      if (next >= 5) {
        if (typeof window !== 'undefined') {
          sessionStorage.setItem('debug-datepicker', '1')
          setDebug(true)
          if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
            alert('Debug mode enabled. Tap the date field again to test.')
          }
        }
        return 0
      }
      return next
    })
  }, [])

  useEffect(() => {
    return () => {
      if (tapTimerRef.current) clearTimeout(tapTimerRef.current)
    }
  }, [])

  useEffect(() => {
    if (debug && isOpen !== undefined && name) {
      console.log('[DateRangePickerControl] isOpen changed:', isOpen, 'name:', String(name))
    }
  }, [debug, isOpen, name])

  const handleTriggerPointerDown = (e: React.PointerEvent) => {
    if (disabled) return
    if (debug) {
      console.log('[DateRangePickerControl] trigger pointerdown', { name, type: e.pointerType })
      if (typeof window !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent)) {
        alert(`Date picker trigger touched (name: ${String(name)})`)
      }
    }
    if (e.pointerType === 'touch') {
      e.preventDefault()
      openedByTouchRef.current = true
      setIsOpen(true)
      setTimeout(() => {
        openedByTouchRef.current = false
      }, 400)
    }
  }

  const handleTriggerClick = () => {
    handleDebugTap()
    if (debug && name) {
      console.log('[DateRangePickerControl] trigger click', { name })
    }
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    onChange({ from: undefined, to: undefined })
    setCalendarKey((prev) => prev + 1)
  }

  const triggerId = id ? `${id}-trigger` : undefined

  return (
    <div className={cn('space-y-1', className)}>
      {label ? (
        <Label htmlFor={triggerId} className="text-sm font-medium">
          {label}
        </Label>
      ) : null}
      <div className="grid gap-2">
        <Popover
          open={disabled ? false : isOpen}
          modal={false}
          onOpenChange={(open) => {
            if (disabled) return
            if (debug && name) {
              console.log('[DateRangePickerControl] onOpenChange', { open, name: String(name) })
            }
            if (openedByTouchRef.current && !open) return
            setIsOpen(open)
          }}
        >
          <PopoverTrigger asChild>
            <Button
              type="button"
              id={triggerId}
              ref={triggerRef}
              variant="outline"
              showLoader={false}
              disabled={disabled}
              onPointerDown={handleTriggerPointerDown}
              onClick={handleTriggerClick}
              className={cn(
                'w-full justify-between text-left font-normal text-foreground border-input focus-visible:ring-1 hover:bg-transparent',
                {
                  'text-muted-foreground': !value?.from,
                  'border-primary': isOpen,
                  'bg-destructive/10 focus:border-destructive': error,
                  'border-destructive': isOpen && error,
                },
                triggerClassName
              )}
            >
              {value?.from ? (
                value.to ? (
                  <>
                    {format(value.from, 'LLL dd, y')} - {format(value.to, 'LLL dd, y')}
                  </>
                ) : (
                  format(value.from, 'LLL dd, y')
                )
              ) : (
                <span>{placeholder}</span>
              )}
              <div className="flex items-center gap-2">
                {value?.from && !disabled && (
                  <div
                    role="button"
                    tabIndex={0}
                    className="h-4 w-4 rounded-full hover:bg-destructive/10 flex items-center justify-center cursor-pointer"
                    onClick={handleClear}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        handleClear(e as unknown as React.MouseEvent)
                      }
                    }}
                  >
                    <X className="h-3 w-3" />
                  </div>
                )}
                <CalendarIcon className="text-primary" />
              </div>
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-auto p-0 max-h-[85vh] overflow-y-auto"
            align="start"
            side={isMobile ? 'top' : 'bottom'}
            sideOffset={isMobile ? 8 : 4}
            data-date-picker="true"
          >
            <Calendar
              key={calendarKey}
              initialFocus
              mode="range"
              defaultMonth={value?.from || new Date()}
              selected={
                value?.from
                  ? ({ from: value.from, to: value.to } as DateRange)
                  : undefined
              }
              onSelect={(next) => {
                if (value?.from && value?.to) {
                  if (next?.from && !next?.to) {
                    onChange({
                      from: next.from,
                      to: undefined,
                    })
                  } else if (next?.from && next?.to) {
                    onChange({
                      from: next.from,
                      to: next.to,
                    })
                  }
                } else {
                  onChange({
                    from: next?.from ?? undefined,
                    to: next?.to ?? undefined,
                  })
                }
              }}
              numberOfMonths={isMobile ? 1 : 2}
            />
          </PopoverContent>
        </Popover>
      </div>
    </div>
  )
}
